import { generateAutofillEdges } from './edge-generators/autofill.edge-generators'
import { generateConcatenationEdges } from './edge-generators/concat.edge-generators'
import { generateLinkEdges } from './edge-generators/link.edge-generators'
import { generateListEdges } from './edge-generators/list.edge-generators'
import { generateTextEdges } from './edge-generators/text.edge-generators'
import { CycleDetectorVisitor } from './traversers/CycleDetectorVisitor'
import { DescendantVisitor } from './traversers/DescendantVisitor'
import { depthFirstSearch } from './traversers/depth-first-search'

export function generateDAG(templateFields) {
  const dag = new DAG(templateFields)
  return dag
}

class DAG {
  constructor(templateFields) {
    this.meta = generateMetadata(templateFields)
    this.nodes = {}
    this.edges = {}
    this.descendantTemplateFieldsByFieldId = {}

    // generate graph
    templateFields.forEach(templateField => {
      // set the node
      this.nodes[templateField.field_id] = templateField

      // generate the edges and add them
      const edges = generateEdges(templateField, templateFields, this.meta)

      // add the edge
      edges.forEach(edge => this.addEdge(edge))
    })

    // TODO: maybe these should be in the data container instead of here?
    // Would make sense to keep the DAG just to the graph and the visitors external

    // check for cycles
    this.hasCycle = Object.values(this.nodes).reduce((memo, node) => {
      const visitor = new CycleDetectorVisitor(node)
      depthFirstSearch(this, node, visitor)
      return memo || visitor.getCycleDetected()
    }, false)

    if (this.hasCycle) {
      console.warn('[CLARAVINE] Cycle detected in template DAG')
      return
    }

    // Note: this must be done *after* edges have been generated.  If edges change, it must be rebuilt
    // visit each node and do a traversal to build the linear dependencies for each node
    this.descendantTemplateFieldsByFieldId = Object.values(this.nodes).reduce((memo, node) => {
      const visitor = new DescendantVisitor(node)
      depthFirstSearch(this, node, visitor)
      memo[node.field_id] = visitor.getDescendantPath()
      return memo
    }, {})
  }

  addEdge(edge) {
    if (!edge.from || !edge.to || edge.from === edge.to) {
      console.warn('[CLARAVINE] Attempt to add invalid edge', edge)
      return
    }

    if (!this.edges[edge.from]) {
      this.edges[edge.from] = []
    }

    // only add if it isn't already there
    if (!this.edges[edge.from].includes(edge.to)) {
      this.edges[edge.from].push(edge.to)
    }
  }

  getAllDescendants(fieldId) {
    return this.descendantTemplateFieldsByFieldId[fieldId] || []
  }
}

function generateEdges(templateField, templateFields, metadata) {
  const isVariable = templateField.field && templateField.field.segment_type === 'variable'
  const gov = isVariable ? templateField : templateField.field || {}

  // process the edges
  const edgeGenerator = EDGE_GENERATORS[gov.governance_type]

  const edges = edgeGenerator && edgeGenerator(templateField, templateFields, metadata)

  // return the generated edges
  return edges || []
}

const EDGE_GENERATORS = {
  list: generateListEdges,
  autofill: generateAutofillEdges,
  concatenation: generateConcatenationEdges,
  link: generateLinkEdges,
  text: generateTextEdges,
  // variables
  //   - list
  //   - auto fill
  // pattern?
}

function getColumnsByListId(templateColumns) {
  //
  const columnsByListId = templateColumns.reduce((memo, column) => {
    const gov =
      column && column.field && column.field.segment_type === 'variable'
        ? column
        : column.field || {}
    const listGovernance = gov.list_governance
    if (listGovernance) {
      const listId = listGovernance.list_id
      if (!memo[listId]) {
        memo[listId] = []
      }
      memo[listId].push(column)
    }
    return memo
  }, {})

  // sort each of the column lists
  Object.values(columnsByListId).forEach(columnsInList => {
    columnsInList.sort(orderByColumnPrecedenceAsc)
  })

  return columnsByListId
}

function orderByColumnPrecedenceAsc(a, b) {
  const aGov = a.field.segment_type === 'variable' ? a : a.field
  const bGov = b.field.segment_type === 'variable' ? b : b.field
  const aListColumn = aGov.list_governance && aGov.list_governance.list_column
  const bListColumn = bGov.list_governance && bGov.list_governance.list_column
  const aPrecedence = aListColumn ? aListColumn.precedence : 0
  const bPrecedence = bListColumn ? bListColumn.precedence : 0
  return aPrecedence - bPrecedence
}

function generateMetadata(templateFields) {
  const columnsByListId = getColumnsByListId(templateFields)

  return {
    columnsByListId,
  }
}
